import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type UIEvent } from "react"
import type { AdminGarageRecord } from "../types/api"
import { useI18n } from "../hooks/useI18n"
import { adminUi } from "../i18n/adminUi"
import { FigmaLicensePlate } from "./MainFigmaVisualsExact"
import "./AdminArchiveSheet.css"

type Props = {
  mode: "archive" | "search"
  records: AdminGarageRecord[]
  target: AdminGarageRecord | null
  armed: boolean
  busy?: boolean
  nextCursor?: string | null
  visualScrollTop?: number
  onArm: () => void
  onSelect: (record: AdminGarageRecord) => void
  onBack: () => void
  onConfirm: () => void
  onLoadMore?: () => void
}

function ArchiveRecord({ record, target, armed, selectable, onArm, onSelect }: {
  record: AdminGarageRecord
  target: boolean
  armed: boolean
  selectable: boolean
  onArm: () => void
  onSelect: () => void
}) {
  const { locale, direction } = useI18n()
  const a = (key: Parameters<typeof adminUi>[1]) => adminUi(locale, key)
  const formattedDate = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(record.continuous_activation_started_at))
  return (
    <div
      className={`bbf-admin-archive-record ${target ? "target" : ""} ${selectable ? "selectable" : ""}`}
      onClick={selectable ? onSelect : undefined}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      onKeyDown={selectable ? (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onSelect()
        }
      } : undefined}
    >
      <FigmaLicensePlate plate={record} />
      {target ? (
        <button
          type="button"
          className={`bbf-admin-unlink ${armed ? "armed" : ""}`}
          onClick={(event) => {
            event.stopPropagation()
            onArm()
          }}
        >
          <span className="bbf-admin-unlink-visual">
            <span className="bbf-admin-unlink-label" dir={direction}>{a("unlink")}</span>
          </span>
        </button>
      ) : (
        <div className="bbf-admin-record-meta" dir={direction}>
          <span>{a("activationDate")}</span>
          <span>{formattedDate}</span>
          <span>{a("renewals")}: {record.continuous_renewals_count}</span>
        </div>
      )}
    </div>
  )
}

export function AdminArchiveSheet(props: Props) {
  const { locale, direction, t } = useI18n()
  const a = (key: Parameters<typeof adminUi>[1]) => adminUi(locale, key)
  const viewportRef = useRef<HTMLDivElement>(null)
  const dragStartY = useRef<number | null>(null)
  const dragged = useRef(false)
  const [scrollTop, setScrollTop] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [dragging, setDragging] = useState(false)
  const records = useMemo(() => {
    if (props.mode !== "search" || !props.target) return props.records
    if (props.records.some((item) => item.garage_id === props.target?.garage_id)) return props.records
    return [...props.records.slice(0, 4), props.target, ...props.records.slice(4)]
  }, [props.mode, props.records, props.target])

  useEffect(() => {
    if (props.visualScrollTop == null || !viewportRef.current) return
    viewportRef.current.scrollTop = props.visualScrollTop
    setScrollTop(props.visualScrollTop)
  }, [props.visualScrollTop])

  const onScroll = (event: UIEvent<HTMLDivElement>) => {
    const element = event.currentTarget
    setScrollTop(element.scrollTop)
    if (props.nextCursor && element.scrollTop + element.clientHeight >= element.scrollHeight - 40) props.onLoadMore?.()
  }

  const beginGrabberDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    dragStartY.current = event.clientY
    dragged.current = false
    setDragging(true)
    setDragOffset(0)
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  const moveGrabberDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (dragStartY.current == null) return
    const delta = Math.max(0, event.clientY - dragStartY.current)
    if (delta > 3) dragged.current = true
    setDragOffset(delta)
  }

  const finishGrabberDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (dragStartY.current == null) return
    const delta = Math.max(0, event.clientY - dragStartY.current)
    dragStartY.current = null
    setDragging(false)
    setDragOffset(0)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
    if (delta >= 36) props.onBack()
  }

  const cancelGrabberDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    dragStartY.current = null
    dragged.current = false
    setDragging(false)
    setDragOffset(0)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId)
  }

  const clickGrabber = () => {
    if (dragged.current) {
      dragged.current = false
      return
    }
    props.onBack()
  }

  return (
    <section
      className={`bbf-admin-archive-sheet ${props.mode} ${dragging ? "dragging" : ""}`}
      aria-label={props.mode === "archive" ? a("archiveAria") : a("searchResultAria")}
      style={{ transform: `translateY(${dragOffset}px)` }}
    >
      <button
        type="button"
        className="bbf-admin-sheet-grabber"
        aria-label={a("close")}
        onClick={clickGrabber}
        onPointerDown={beginGrabberDrag}
        onPointerMove={moveGrabberDrag}
        onPointerUp={finishGrabberDrag}
        onPointerCancel={cancelGrabberDrag}
        style={{
          top: 0,
          width: "64px",
          height: "26px",
          margin: 0,
          padding: 0,
          border: 0,
          background: "transparent",
          touchAction: "none",
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: "50%",
            top: "8px",
            width: "36px",
            height: "4px",
            borderRadius: "4px",
            background: "var(--bb-figma-text-muted)",
            transform: "translateX(-50%)",
            pointerEvents: "none",
          }}
        />
      </button>
      <div className="bbf-admin-archive-viewport" ref={viewportRef} onScroll={onScroll}>
        <div className="bbf-admin-archive-content">
          {records.map((record) => (
            <ArchiveRecord
              key={record.garage_id}
              record={record}
              target={props.mode === "search" && record.garage_id === props.target?.garage_id}
              armed={props.armed}
              selectable={!props.busy}
              onArm={props.onArm}
              onSelect={() => props.onSelect(record)}
            />
          ))}
        </div>
      </div>
      {scrollTop > 0 && <span className="bbf-admin-scroll-fade top" aria-hidden="true" />}
      <span className="bbf-admin-scroll-fade bottom" aria-hidden="true" />
      {props.mode === "search" && (
        <footer className="bbf-admin-sheet-footer">
          <button type="button" className="back" onClick={props.onBack} dir={direction}>{t("common.back")}</button>
          <button type="button" className="confirm" disabled={!props.armed || props.busy} onClick={props.onConfirm} dir={direction}>{a("confirm")}</button>
        </footer>
      )}
    </section>
  )
}
